import { Trans, useTranslations } from '@aikoa/i18n/react'
import { useMutation } from '@tanstack/react-query'
import { useCallback, useMemo, useRef, useState } from 'react'
import { View } from 'react-native'
import Animated, { FadeInUp } from 'react-native-reanimated'
import { OtpVerification } from '~/components/auth/OtpVerification'
import {
  type ContactForConsent,
  type FeedbackFlowData,
} from '~/components/feedback/feedbackFlowTypes'
import { useFeedbackConsentOtp } from '~/components/feedback/useFeedbackConsentOtp'
import { useFlowManager } from '~/components/flows/useFlowManager'
import { LoadingView } from '~/components/LoadingView'
import { ScrollView } from '~/components/ScrollView'
import { Body, Label } from '~/components/typography/Content'
import { Heading1 } from '~/components/typography/Titles'
import BottomSheet from '~/components/ui/bottom-sheet'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Text } from '~/components/ui/text'
import { trpc } from '~/lib/trpc'

export function FeedbackConsentAuth({
  contact,
  isOpen,
  feedbackId,
  nonce,
}: {
  contact: ContactForConsent | null
  isOpen: boolean
  feedbackId?: string | null
  nonce?: string | null
}) {
  const { t } = useTranslations('feedback')

  const {
    data,
    updateData,
    completeFlow,
    previousPhase,
    isLoading: isFlowLoading,
    setIsLoading: setFlowIsLoading,
  } = useFlowManager()

  const updateContactMutation = useMutation(
    trpc.contacts.updateContactFromFeedback.mutationOptions()
  )

  const hasCompletedAuthRef = useRef(false)

  const hasExistingContact = !!contact?.id
  const contactHasPhone = !!contact?.phone
  const isFormRequired = !hasExistingContact || !contactHasPhone

  const flowData = data as Partial<FeedbackFlowData>
  const initialName = flowData.contactDetails?.name ?? contact?.name ?? ''
  const initialPhone = flowData.contactDetails?.phone ?? contact?.phone ?? ''

  const [showContactForm, setShowContactForm] = useState(isFormRequired)
  const [contactName, setContactName] = useState(initialName)
  const [contactPhone, setContactPhone] = useState(initialPhone)

  const handleNameChange = useCallback(
    (name: string) => {
      setContactName(name)
      updateData('contactDetails', (current) => ({ ...current, name }))
    },
    [updateData]
  )

  const handlePhoneChange = useCallback(
    (text: string, _: unknown, storedPhone: string) => {
      setContactPhone(storedPhone)
      updateData('contactDetails', (current) => ({ ...current, phone: storedPhone }))
    },
    [updateData]
  )

  const handleCancelForm = useCallback(() => {
    const originalName = contact?.name ?? ''
    const originalPhone = contact?.phone ?? ''

    setContactName(originalName)
    setContactPhone(originalPhone)

    updateData('contactDetails', (current) => ({
      ...current,
      name: originalName || undefined,
      phone: originalPhone || undefined,
    }))

    setShowContactForm(false)
  }, [contact, updateData])

  const effectiveContact = useMemo(
    (): ContactForConsent => ({
      id: contact?.id ?? '',
      name: contactName || contact?.name || '',
      email: null,
      phone: contactPhone || null,
      language: contact?.language ?? 'fi',
      disabled_at: null,
      disabled_reason: null,
    }),
    [contact, contactName, contactPhone]
  )

  const handleCompleteFlow = useCallback(async () => {
    hasCompletedAuthRef.current = true

    if (contact?.id) {
      const originalName = contact.name ?? ''
      const originalPhone = contact.phone ?? ''
      const nameChanged = contactName.trim() !== originalName.trim()
      const phoneChanged = !!contactPhone && contactPhone !== originalPhone

      if (nameChanged || phoneChanged) {
        void updateContactMutation
          .mutateAsync({
            contactId: contact.id,
            name: nameChanged ? contactName || undefined : undefined,
            phone: phoneChanged ? contactPhone || undefined : undefined,
          })
          .catch(() => undefined)
      }
    }

    return completeFlow()
  }, [completeFlow, contact, contactName, contactPhone, updateContactMutation])

  const {
    contactMethod,
    showOtpPhase,
    isLoading,
    isVerifying,
    sendOtp,
    handleResendOtp,
    otpStateManager,
    otp,
    setOtp,
  } = useFeedbackConsentOtp({
    contact: effectiveContact,
    feedbackId,
    nonce,
    isFlowLoading,
    onCompleteFlow: handleCompleteFlow,
    setFlowIsLoading,
  })

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !hasCompletedAuthRef.current) {
        previousPhase()
      }
    },
    [previousPhase]
  )

  const isNameValid = contactName.trim().length >= 2
  const isFormValid = isNameValid && !!contactMethod

  return (
    <BottomSheet
      modalKey="feedback-consent"
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      showHandle={false}
      showCloseButton={false}>
      <ScrollView
        className="max-h-[85vh] min-h-[350px] flex-1"
        contentContainerClassName="gap-6 px-6 pb-6 pt-8"
        footerClassName="py-0 bottom-6!"
        footer={
          <Button variant="ghost" size="lg" onPress={previousPhase} disabled={isVerifying}>
            {t(($) => $.flow.back)}
          </Button>
        }>
        <View className="gap-3">
          <Heading1 className="text-center">{t(($) => $.consent.title)}</Heading1>

          <Body className="text-center">
            <Trans
              t={t}
              i18nKey={($) => $.consent.description}
              components={{ strong: <Text className="font-semibold" /> }}
            />
          </Body>
        </View>

        {showOtpPhase && contactMethod ? (
          <Animated.View
            className="w-full items-center gap-4 rounded-2xl border border-tertiary-foreground/90 bg-tertiary-foreground/40 px-5 py-6"
            entering={FadeInUp.duration(150)}>
            <View className="gap-2">
              <Body className="text-center text-sm">
                {t(($) => $.consent.otpSent)}{' '}
                {contactMethod.type === 'email'
                  ? t(($) => $.consent.otpSentEmail)
                  : t(($) => $.consent.otpSentPhone)}
              </Body>
              <Body className="text-center font-medium">{contactMethod.value}</Body>
            </View>

            <OtpVerification
              otp={otp}
              setOtp={setOtp}
              disabled={isVerifying}
              inputTestId="feedback-consent-otp-input"
              key={`otp-${contactMethod.type}-${contactMethod.value}`}
              onResend={handleResendOtp}
              otpStateManager={otpStateManager}
              pendingChange={contactMethod.value}
            />

            {isVerifying && (
              <Animated.View className="items-center gap-2" entering={FadeInUp.duration(150)}>
                <LoadingView className="flex-none" />
                <Body className="text-center text-sm text-foreground/70">
                  {t(($) => $.consent.verifying)}
                </Body>
              </Animated.View>
            )}
          </Animated.View>
        ) : showContactForm ? (
          <Animated.View className="w-full gap-5" entering={FadeInUp.duration(150)}>
            <View className="gap-1">
              <Label>{t(($) => $.consent.formNameLabel)}</Label>
              <Input
                testID="feedback-consent-name-input"
                value={contactName}
                onChangeText={handleNameChange}
                autoFocus={isFormRequired}
                autoCapitalize="words"
                autoCorrect={false}
              />
            </View>

            <View className="gap-1">
              <Label>{t(($) => $.consent.formPhoneLabel)}</Label>
              <Input
                testID="feedback-consent-phone-input"
                type="phone"
                value={contactPhone}
                onChangeText={handlePhoneChange}
                keyboardType="phone-pad"
              />
            </View>

            <View
              className={
                isFormRequired ? 'items-center' : 'flex-row items-center justify-center gap-4'
              }>
              {!isFormRequired && (
                <Button variant="ghost" size="default" onPress={handleCancelForm}>
                  {t(($) => $.consent.editContactValueCancel)}
                </Button>
              )}

              <Button
                testID="feedback-consent-send-otp"
                variant="primary"
                size={isFormRequired ? 'lg' : 'default'}
                onPress={() => sendOtp()}
                disabled={!isFormValid || isLoading}>
                {t(($) => $.consent.otpButtonLabel)}
              </Button>
            </View>
          </Animated.View>
        ) : (
          <View className="items-center gap-4">
            <Button
              testID="feedback-consent-send-otp"
              variant="primary"
              size="lg"
              onPress={() => sendOtp()}
              disabled={isLoading}>
              {t(($) => $.consent.otpButtonLabel)}
            </Button>

            <View className="items-center gap-1">
              <Body className="text-center text-sm text-foreground/70">
                {t(($) => $.consent.otpSendDescription, {
                  type: t(($) => $.consent.otpSendDescriptionPhone),
                  value: contactMethod?.value ?? '',
                })}
              </Body>

              <Button
                testID="feedback-consent-edit-contact"
                variant="link"
                size="sm"
                onPress={() => setShowContactForm(true)}
                disabled={isLoading}>
                {t(($) => $.consent.updateContactDetails)}
              </Button>
            </View>
          </View>
        )}
      </ScrollView>
    </BottomSheet>
  )
}
